import React, { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer, useLocalObservable } from "mobx-react";
import PageTitle from "../../components/common/pages/PageTitle";
import AppButton from "../../components/common/buttons/AppButton";
import FormTextField from "../../components/common/form/FormTextField";
import CustomTypography from "../../components/common/form/CustomTypography";
import CategoryTable from "./CategoryTable";
import CategoryFormModal from "./CategoryFormModal";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { useNotification } from "../../../utils/NotificationsContext";
import {
  CATEGORY_SORT_BY_OPTIONS,
  GetCategoriesParams,
  normalizeCategoryListSortBy,
} from "../../../models/Categories/types";
import { SortType } from "../../../models/SortType";

function parseSortDirectionFromSearch(searchParams: URLSearchParams): SortType {
  const dirRaw =
    searchParams.get("sort_direction") ||
    searchParams.get("sort_order") ||
    searchParams.get("sort_type");
  const dirNorm = dirRaw?.trim().toUpperCase();
  if (!dirNorm) return SortType.ASC;
  return dirNorm === "ASC" ? SortType.ASC : SortType.DESC;
}

function parseActiveFromSearch(searchParams: URLSearchParams): boolean | undefined {
  const a = searchParams.get("active");
  if (a === "1" || a === "true") return true;
  if (a === "0" || a === "false") return false;
  return undefined;
}

/** Single source of truth for URL query ↔ list params (avoids stale React state vs address bar). */
function getParamsFromSearch(search: string): GetCategoriesParams {
  const searchParams = new URLSearchParams(search);
  const pageRaw = searchParams.get("page");
  const limitRaw = searchParams.get("limit");
  return {
    page_no: pageRaw ? parseInt(pageRaw, 10) || 1 : 1,
    limit: limitRaw ? parseInt(limitRaw, 10) || 30 : 30,
    sort_by: normalizeCategoryListSortBy(searchParams.get("sort_by")),
    sort_type: parseSortDirectionFromSearch(searchParams),
    search:
      searchParams.get("search") ||
      searchParams.get("name") ||
      undefined,
    active: parseActiveFromSearch(searchParams),
  };
}

const CategoryListing: React.FC = () => {
  const theme = useTheme();
  const { categoryStore, userStore } = useStores();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const listing = useLocalObservable(() => {
    const initial = getParamsFromSearch(location.search);
    return {
      params: initial,
      searchTerm: initial.search || "",
      isFiltersExpanded: false,
      isLoading: false,
      categoryModalOpen: false,
      categoryModalEditId: null as number | null,
      setParams(updater: (prev: GetCategoriesParams) => GetCategoriesParams) {
        this.params = updater(this.params);
      },
      syncFromLocationSearch(search: string) {
        const next = getParamsFromSearch(search);
        const prev = this.params;
        if (
          prev.page_no === next.page_no &&
          (prev.limit ?? 30) === (next.limit ?? 30) &&
          normalizeCategoryListSortBy(prev.sort_by) === next.sort_by &&
          (prev.sort_type ?? SortType.ASC) ===
            (next.sort_type ?? SortType.ASC) &&
          (prev.search ?? "") === (next.search ?? "") &&
          prev.active === next.active
        ) {
          // keep prev reference like setState(prev => prev)
        } else {
          this.params = next;
        }
        const term = next.search ?? "";
        if (this.searchTerm !== term) this.searchTerm = term;
      },
    };
  });

  const refreshListing = () => {
    categoryStore.listCategories(listing.params).catch((err: any) => {
      showError(
        "Error",
        err?.response?.data?.message ||
          categoryStore.error ||
          "Failed to load categories.",
      );
    });
  };

  useEffect(() => {
    listing.isLoading = true;
    categoryStore
      .listCategories(listing.params)
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            categoryStore.error ||
            "Failed to load categories.",
        );
      })
      .finally(() => {
        listing.isLoading = false;
      });
    /**
     * Omit `showError` from deps: it is recreated every `NotificationProvider` render, so
     * including it retriggers this effect and fires duplicate `/v1/categories` requests.
     * Same pattern as `LeadsListing` (intentional exhaustive-deps exception).
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unstable showError from context
  }, [listing.params, categoryStore]);

  /**
   * Push filter/sort/page into the URL as soon as `listing.params` change.
   * `useLayoutEffect` so `location.search` is updated before other effects run — a
   * following "sync from URL" effect could read a stale `location.search` before `navigate`
   * committed, which broke updating the bar from submitted search. Back/forward is handled below.
   */
  useLayoutEffect(() => {
    const q = new URLSearchParams();
    q.set("page", String(listing.params.page_no));
    if (listing.params.limit) q.set("limit", String(listing.params.limit));
    q.set(
      "sort_by",
      normalizeCategoryListSortBy(listing.params.sort_by),
    );
    q.set(
      "sort_direction",
      listing.params.sort_type === SortType.ASC ? "ASC" : "DESC",
    );
    if (listing.params.search) q.set("search", listing.params.search);
    if (listing.params.active !== undefined)
      q.set("active", listing.params.active ? "1" : "0");
    navigate({ search: q.toString() }, { replace: true });
  }, [listing.params, navigate]);

  /** Keep list state in sync when the address bar changes (browser back/forward, external links). */
  useEffect(() => {
    listing.syncFromLocationSearch(location.search);
  }, [location.search]);

  const handleSearchSubmit = () => {
    const trimmed = listing.searchTerm.trim();
    listing.setParams((prev) => ({
      ...prev,
      search: trimmed || undefined,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearchSubmit();
  };

  const resetFilters = () => {
    listing.searchTerm = "";
    listing.params = {
      page_no: 1,
      limit: 30,
      sort_by: "name",
      sort_type: SortType.ASC,
      search: undefined,
      active: undefined,
    };
  };

  const handleAddCategory = () => {
    listing.categoryModalEditId = null;
    listing.categoryModalOpen = true;
  };

  const handleOpenCategoryEdit = (id: number) => {
    listing.categoryModalEditId = id;
    listing.categoryModalOpen = true;
  };

  const handleCategoryModalSuccess = () => {
    refreshListing();
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) =>
    listing.setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));

  const handleRowsPerPageChange = (newLimit: number) => {
    listing.setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Paper>
        <Box
          sx={{
            marginTop: "0.6rem",
            paddingX: 4,
            paddingY: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
            borderRadius: "4px",
            minHeight: { xs: "60vh", md: typeof window !== "undefined" ? window.innerHeight - 110 : "70vh" },
          }}
        >
          <Box sx={{ maxHeight: typeof window !== "undefined" ? window.innerHeight - 180 : 600 }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingBottom: 2,
                }}
              >
                <PageTitle title="Categories" />
                {userStore.hasPermission(EmployeeRoles.EDIT_CATEGORY, true) ? (
                  <AppButton
                    variant="primary"
                    label="Add Category"
                    onClick={handleAddCategory}
                  />
                ) : null}
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <Box
                  onClick={() =>
                    (listing.isFiltersExpanded = !listing.isFiltersExpanded)
                  }
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 0.5,
                    cursor: "pointer",
                    borderBottom: listing.isFiltersExpanded
                      ? "1px solid"
                      : "none",
                    borderColor: "divider",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <CustomTypography variant="heading7">
                    Search & Filters
                  </CustomTypography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      listing.isFiltersExpanded =
                        !listing.isFiltersExpanded;
                    }}
                  >
                    {listing.isFiltersExpanded ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </IconButton>
                </Box>
                <Collapse
                  in={listing.isFiltersExpanded}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}>
                        <FormTextField
                          id="category-search"
                          placeholder="Search by category name or Id"
                          type="search"
                          variant="outlined"
                          sx={{ mb: 0 }}
                          value={listing.searchTerm}
                          onChange={(_, value) => {
                            listing.searchTerm = value || "";
                          }}
                          onKeyDown={handleKeyDown}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          flex: { xs: "1 1 100%", sm: "0 0 auto" },
                          minWidth: { xs: "100%", sm: "auto" },
                        }}
                      >
                        <AppButton
                          variant="primary"
                          label="Search"
                          onClick={handleSearchSubmit}
                          customStyles={{
                            flex: { xs: 1, sm: "unset" },
                            minWidth: { xs: 0, sm: 140 },
                            height: 40,
                          }}
                        />
                        <AppButton
                          variant="transparentPrimary"
                          label="Clear"
                          onClick={resetFilters}
                          customStyles={{
                            flex: { xs: 1, sm: "unset" },
                            minWidth: { xs: 0, sm: 120 },
                            height: 40,
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                        "& > .MuiFormControl-root": {
                          minWidth: { xs: "100%", sm: 140 },
                          flex: { xs: "1 1 100%", sm: "0 0 auto" },
                        },
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: { sm: 140 } }}>
                        <InputLabel id="cat-sort-by">Sort by</InputLabel>
                        <Select
                          labelId="cat-sort-by"
                          label="Sort by"
                          value={normalizeCategoryListSortBy(
                            listing.params.sort_by,
                          )}
                          onChange={(e) =>
                            listing.setParams((p) => ({
                              ...p,
                              sort_by: normalizeCategoryListSortBy(
                                String(e.target.value),
                              ),
                              page_no: 1,
                            }))
                          }
                        >
                          {CATEGORY_SORT_BY_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: { sm: 150 } }}>
                        <InputLabel id="cat-sort-dir">Direction</InputLabel>
                        <Select
                          labelId="cat-sort-dir"
                          label="Direction"
                          value={listing.params.sort_type ?? SortType.ASC}
                          onChange={(e) =>
                            listing.setParams((p) => ({
                              ...p,
                              sort_type: e.target.value as SortType,
                              page_no: 1,
                            }))
                          }
                        >
                          <MenuItem value={SortType.ASC}>Ascending</MenuItem>
                          <MenuItem value={SortType.DESC}>Descending</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: { sm: 150 } }}>
                        <InputLabel id="cat-active">Active</InputLabel>
                        <Select
                          labelId="cat-active"
                          label="Active"
                          value={
                            listing.params.active === undefined
                              ? "all"
                              : listing.params.active
                                ? "1"
                                : "0"
                          }
                          onChange={(e) => {
                            const v = String(e.target.value);
                            listing.setParams((p) => ({
                              ...p,
                              active:
                                v === "all"
                                  ? undefined
                                  : v === "1"
                                    ? true
                                    : false,
                              page_no: 1,
                            }));
                          }}
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="1">Active only</MenuItem>
                          <MenuItem value="0">Inactive only</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Collapse>
              </Paper>
            </Box>
            {listing.isLoading && (
              <Box
                sx={{
                  minHeight: "60vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            )}
            {!listing.isLoading && (
              <Box>
                {categoryStore.categories.length === 0 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="10rem"
                    textAlign="center"
                  >
                    <Typography
                      variant="h6"
                      fontWeight="500"
                      color="text.secondary"
                    >
                      No data found
                    </Typography>
                  </Box>
                )}
                {categoryStore.categories.length > 0 && (
                  <CategoryTable
                    maxHeight={
                      typeof window !== "undefined"
                        ? window.innerHeight - 350
                        : 480
                    }
                    categories={categoryStore.categories}
                    limit={
                      listing.params.limit || categoryStore.limit
                    }
                    page_no={listing.params.page_no}
                    total_count={categoryStore.total_count}
                    sortBy={normalizeCategoryListSortBy(
                      listing.params.sort_by,
                    )}
                    sortType={listing.params.sort_type || SortType.ASC}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    onSortChange={(column, sortTypeNext) =>
                      listing.setParams((prev) => ({
                        ...prev,
                        sort_by: normalizeCategoryListSortBy(column),
                        sort_type: sortTypeNext,
                        page_no: 1,
                      }))
                    }
                    onEditCategory={handleOpenCategoryEdit}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <CategoryFormModal
        open={listing.categoryModalOpen}
        categoryId={listing.categoryModalEditId}
        onClose={() => {
          listing.categoryModalOpen = false;
          listing.categoryModalEditId = null;
        }}
        onSuccess={handleCategoryModalSuccess}
      />
    </Grid2>
  );
};

/** Route entry for `/categories/add-edit-category` (deep links share the same modal as listing). */
export function CategoryAddEditPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const raw = searchParams.get("category-id");
  const categoryId =
    raw != null && raw !== "" && !Number.isNaN(Number(raw))
      ? Number(raw)
      : null;

  return (
    <Box sx={{ minHeight: "42vh", bgcolor: "transparent" }}>
      <CategoryFormModal
        open
        categoryId={categoryId}
        onClose={() => navigate("/categories/listing")}
        onSuccess={() => navigate("/categories/listing")}
      />
    </Box>
  );
}

export default observer(CategoryListing);
